import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, BoxProps, IconButton, Stack, Typography } from '@mui/material';
import {
  Send as SendIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import _, { debounce } from 'lodash';

import { useAppSelector } from '../../../../hooks';
import { typoGradientStyles } from '../../../../helper';
import { TP_COLORS } from '../../../../constants';
import {
  getChatState,
  IMessage,
  sendMessage,
  syncMessages,
  sendConversationTypingData,
} from '../../../../store/V2/ChatSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { TPTextField } from '../../../../components/Inputs';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { formatMessageTime, UnreadMessageElement } from '../chatHelpers';

const commonBoxProps: BoxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
};

const Conversation: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const { user } = useAppSelector(getAuthState);
  const { isLoading, messagesData, selectedConversation } = useAppSelector(
    getChatState
  );

  // State
  // const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isFirstTimePassed, setFirstTimePassed] = useState(false);
  const [isAutoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isConversationUpdated, setConversationUpdated] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [tempTypingData, setTempTypingData] = useState<string[]>([]);

  const stackRef = useRef<HTMLDivElement | null>(null);

  const messages =
    messagesData && messagesData.messages ? messagesData.messages : [];

  let unreadMessageIndex = -1;

  useEffect(() => {
    if (
      selectedConversation &&
      selectedConversation._id &&
      !isConversationUpdated
    ) {
      setConversationUpdated(true);
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (messages.length && isAutoScrollEnabled) {
      checkScroll();
    }
  }, [messages.length]);

  const checkScroll = () => {
    if (stackRef.current) {
      const scrollOptions: ScrollIntoViewOptions = {
        behavior: isFirstTimePassed ? 'smooth' : undefined,
        block: 'nearest',
        inline: 'nearest',
      };

      let unReadElement: HTMLElement | null = null;

      if (unreadMessageIndex > -1) {
        unReadElement = window.document.getElementById(
          `message-item-${unreadMessageIndex}`
        );
      }

      // Scrollable
      if (stackRef.current.scrollHeight > stackRef.current.clientHeight) {
        if (unReadElement) {
          unReadElement.scrollIntoView(scrollOptions);
        } else {
          scrollToBottom(isFirstTimePassed);
        }
      } else {
        // Not scrollable
        updateSeenStatus();
      }

      setFirstTimePassed(true);
    }
  };

  const handleSend = () => {
    if (!selectedConversation?._id) return;

    dispatch(
      sendMessage({
        conversation: selectedConversation._id,
        message: message.trim(),
      })
    );

    setMessage('');
  };

  const scrollToBottom = (smoothScroll = false) => {
    const element = document.getElementById(
      `message-item-${messages.length - 1}`
    );

    if (element) {
      element.scrollIntoView({
        behavior: smoothScroll ? 'smooth' : undefined,
        block: 'end',
        inline: 'nearest',
      });
    }
  };

  const updateSeenStatus = debounce(() => {
    if (messages.length) {
      const ids = messages
        .filter(
          (messageItem) =>
            messageItem.sender._id !== user?._id &&
            messageItem.status.some(
              (item) => item.user === user?._id && item.status !== 'SEEN'
            )
        )
        .map((messageItem) => messageItem._id);

      if (ids.length) {
        dispatch(
          syncMessages({
            ids,
            status: 'SEEN',
          })
        );
      }
    }
  }, 1000);

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    const isBottom: boolean = scrollHeight - scrollTop - 10 < clientHeight;

    // if (scrollTop > lastScrollTop) {
    //   // Scroll Down
    // }

    if (scrollTop < lastScrollTop) {
      // Scroll Up
      setAutoScrollEnabled(false);
    }

    if (isBottom) {
      // Bottom Reached
      setAutoScrollEnabled(true);
      updateSeenStatus();
    }

    setLastScrollTop(scrollTop);
  };

  const handleTyping = () => {
    let timeout: NodeJS.Timeout;

    if (selectedConversation && selectedConversation._id) {
      const _matchingConversationTypingData = tempTypingData.some(
        (item) => item === selectedConversation._id
      );

      if (!isTyping) {
        setIsTyping(true);
        dispatch(
          sendConversationTypingData({
            conversationId: selectedConversation._id,
            typing: true,
          })
        );

        setTempTypingData((prevState) => {
          if (selectedConversation._id) {
            const _newState = _.uniq([...prevState, selectedConversation._id]);

            return _newState;
          }

          return prevState;
        });
      }

      if (!_matchingConversationTypingData) {
        timeout = setTimeout(() => {
          setIsTyping(false);

          if (selectedConversation && selectedConversation._id) {
            dispatch(
              sendConversationTypingData({
                conversationId: selectedConversation._id,
                typing: false,
              })
            );

            setTempTypingData((prevState) => [
              ...prevState.filter((item) => item !== selectedConversation._id),
            ]);
          }
        }, 2000);
      }
    }

    return () => clearTimeout(timeout);
  };

  const renderIcon = ({ status }: IMessage) => {
    const isSaved = status.every((item) => item.status === 'SAVED');
    const isDelivered = status.every((item) => item.status === 'DELIVERED');
    const isSeen = status.every((item) => item.status === 'SEEN');

    if (isSaved) {
      return (
        <DoneIcon
          fontSize="small"
          sx={{
            color: TP_COLORS.gray.darken,
            width: '16px',
            height: '16px',
          }}
        />
      );
    }

    if (isDelivered || isSeen) {
      return (
        <DoneAllIcon
          fontSize="small"
          sx={{
            color: isSeen ? '#0D71D9' : TP_COLORS.gray.darken,
            width: '16px',
            height: '16px',
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Stack
        ref={stackRef}
        spacing="10px"
        flexGrow="1"
        onScroll={handleScroll}
        sx={{
          overflowY: 'auto',
          padding: '16px 16px 0px 16px',
        }}
      >
        <Typography
          fontSize="26px"
          fontWeight="700"
          textAlign="center"
          sx={{ ...typoGradientStyles, padding: '40px 0px' }}
        >
          Connect, learn, and grow together!
        </Typography>
        {isLoading && messages.length === 0 ? <BasicLoader /> : null}
        {messages.map((messageItem, index) => {
          const dateTime = formatMessageTime(messageItem.createdAt);
          const isSender = user?._id === messageItem.sender._id;
          const userCount = selectedConversation?.users?.length ?? 0;
          const isGroupChat = userCount > 2 || userCount === 0;
          const senderName = `${messageItem.sender.firstname} ${
            messageItem.sender.lastname || ''
          }`;

          let unreadAlertElement: React.ReactElement | null = null;

          if (
            user &&
            unreadMessageIndex === -1 &&
            messageItem.status &&
            messageItem.status.some(
              (item) => item.user === user._id && item.status !== 'SEEN'
            )
          ) {
            unreadMessageIndex = index;
          }

          if (unreadMessageIndex === index) {
            unreadAlertElement = <UnreadMessageElement key="unread-elem" />;
          }

          const boxProps: BoxProps = {
            ...commonBoxProps,
            display: 'flex',
            alignItems: 'center',
          };

          if (isSender) {
            boxProps.justifyContent = 'flex-end';
          }

          return [
            unreadAlertElement,
            <Box key={index} {...boxProps} id={`message-item-${index}`}>
              <Box
                sx={{
                  padding: '12px 14px 6px 14px',
                  maxWidth: '220px',
                  minWidth: '70px',
                  backgroundColor: TP_COLORS.listItemGray.light,
                  borderRadius: '20px',
                }}
              >
                {isGroupChat && !isSender && (
                  <Typography
                    fontSize="12px"
                    lineHeight="17px"
                    fontWeight="600"
                    marginBottom="3px"
                    color={TP_COLORS.tutopiyaGreenMain.light}
                  >
                    {senderName}
                  </Typography>
                )}

                <Typography fontSize="14px" lineHeight="17px">
                  {messageItem.message}
                </Typography>
                <Box
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  position="relative"
                  bottom="2px"
                >
                  <Typography fontSize="10px">{dateTime}</Typography>
                  &nbsp;
                  {isSender && renderIcon(messageItem)}
                </Box>
              </Box>
            </Box>,
          ];
        })}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          backgroundColor: TP_COLORS.listItemGray.light,
          borderRadius: '28px',
          padding: '11px',
          margin: '16px',
        }}
      >
        {/* <IconButton size="small" onClick={toggleModal}>
          <AttachFileIcon
            fontSize="small"
            sx={{ color: TP_COLORS.gray.darken }}
          />
        </IconButton> */}

        <TPTextField
          multiline
          fullWidth
          placeholder="Message"
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: '14px',
              marginLeft: '8px',
            },
          }}
          value={message}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setMessage(e.target.value);
          }}
          onKeyDown={(e) => {
            handleTyping();

            if (e.key === 'Enter' && !e.shiftKey && message.trim().length) {
              e.preventDefault();
              handleSend();
            }
          }}
        />

        <IconButton
          size="small"
          onClick={handleSend}
          disabled={!message.trim().length}
        >
          <SendIcon sx={{ color: '#0D71D9' }} />
        </IconButton>
      </Stack>
    </>
  );
};

export default Conversation;
